import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type UnitRow = {
  id: string;
  titleLight?: string | null;
  titleAccent?: string | null;
  ctaLabel?: string | null;
  ctaHref?: string | null;
  columnsCount?: number;
  showDividers?: boolean;
  isActive: boolean;
  updatedAt: string;
};

type ItemRow = {
  id: string;
  medicalUnitsId: string;
  orderIndex: number;
  title: string;
  text: string;
  iconUrl?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const MedicalUnitsYonetim: React.FC = () => {
  const resolveMedia = (u?: string | null) => {
    if (!u) return '';
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const path = uuidRe.test(u) ? `/api/media/${u}` : u;
    return toUrl(path);
  };
  const normalizeMediaForSave = (u?: string | null) => {
    if (!u) return null;
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? m[1] : u;
  };

  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 32 }) => {
    const baseIn = src || '';
    const uuidMatch = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = uuidMatch ? `/api/media/${uuidMatch[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const current = el.getAttribute('src') || '';
          const next = current === pref ? raw : pref;
          if (current !== next) {
            el.src = next; setCurrentSrc(next);
          }
        }}
      />
    );
  };

  // Units state
  const [units, setUnits] = React.useState<UnitRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');

  // Unit edit
  const [editingId, setEditingId] = React.useState('');
  const [eTitleLight, setETitleLight] = React.useState('');
  const [eTitleAccent, setETitleAccent] = React.useState('');
  const [eCtaLabel, setECtaLabel] = React.useState('');
  const [eCtaHref, setECtaHref] = React.useState('');
  const [eColumns, setEColumns] = React.useState<number>(0);
  const [eDividers, setEDividers] = React.useState<boolean>(true);
  const [eActive, setEActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  // Unit delete
  const [delId, setDelId] = React.useState('');
  const [delLoading, setDelLoading] = React.useState(false);

  // Selected unit items
  const [selectedUnit, setSelectedUnit] = React.useState<UnitRow | null>(null);
  const [items, setItems] = React.useState<ItemRow[]>([]);
  const [loadingItems, setLoadingItems] = React.useState(false);

  // Item edit/create
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [cTitle, setCTitle] = React.useState('');
  const [cText, setCText] = React.useState('');
  const [cOrder, setCOrder] = React.useState<number>(0);
  const [cActive, setCActive] = React.useState(true);
  const [cIcon, setCIcon] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const [editingItemId, setEditingItemId] = React.useState('');
  const [eITitle, setEITitle] = React.useState('');
  const [eIText, setEIText] = React.useState('');
  const [eIOrder, setEIOrder] = React.useState<number>(0);
  const [eIActive, setEIActive] = React.useState(true);
  const [eIIcon, setEIIcon] = React.useState('');
  const [savingItem, setSavingItem] = React.useState(false);
  const [delItemId, setDelItemId] = React.useState('');
  const [delItemLoading, setDelItemLoading] = React.useState(false);

  const fetchUnits = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl('/api/medical-units/admin'));
      if (!res.ok) throw new Error('Tıbbi birimler alınamadı');
      const data = await res.json();
      setUnits(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchItems = React.useCallback(async (unitId: string) => {
    try {
      setLoadingItems(true); setError('');
      const res = await fetch(toUrl(`/api/medical-units/admin/${unitId}/items`));
      if (!res.ok) throw new Error('Öğeler alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingItems(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchUnits(); fetchMedia(); }, [fetchUnits, fetchMedia]);

  const filtered = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return units;
    return units.filter(u => (
      (u.titleLight || '').toLowerCase().includes(t) ||
      (u.titleAccent || '').toLowerCase().includes(t) ||
      (u.ctaLabel || '').toLowerCase().includes(t)
    ));
  }, [units, q]);

  const openEdit = (row: UnitRow) => {
    setEditingId(row.id);
    setETitleLight(row.titleLight || '');
    setETitleAccent(row.titleAccent || '');
    setECtaLabel(row.ctaLabel || '');
    setECtaHref(row.ctaHref || '');
    setEColumns(typeof row.columnsCount === 'number' ? row.columnsCount : 0);
    setEDividers(!!row.showDividers);
    setEActive(!!row.isActive);
  };
  const cancelEdit = () => { setEditingId(''); setETitleLight(''); setETitleAccent(''); setECtaLabel(''); setECtaHref(''); setEColumns(0); setEDividers(true); setEActive(true); };
  const onSave = async (id: string) => {
    if (!eTitleLight.trim() && !eTitleAccent.trim()) { setError('Lütfen başlık alanlarını doldurun'); return; }
    try {
      setSaving(true); setError('');
      const body: any = {
        titleLight: eTitleLight || null,
        titleAccent: eTitleAccent || null,
        ctaLabel: eCtaLabel || null,
        ctaHref: eCtaHref || null,
        columnsCount: eColumns || 0,
        showDividers: !!eDividers,
        isActive: !!eActive,
      };
      const res = await fetch(toUrl(`/api/medical-units/admin/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchUnits();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const onDelete = (id: string) => setDelId(id);
  const confirmDelete = async () => {
    if (!delId) return;
    try {
      setDelLoading(true);
      const res = await fetch(toUrl(`/api/medical-units/admin/${delId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setDelId(''); await fetchUnits();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelLoading(false); }
  };

  const openItems = (row: UnitRow) => { setSelectedUnit(row); fetchItems(row.id); setCreateOpen(false); };

  const resetCreateItem = () => { setCTitle(''); setCText(''); setCOrder(0); setCActive(true); setCIcon(''); };
  const onCreateItem = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedUnit) return;
    if (!cTitle.trim() || !cText.trim()) { setError('Lütfen başlık ve metin girin'); return; }
    try {
      setCreating(true); setError('');
      const body: any = { medicalUnitsId: selectedUnit.id, orderIndex: cOrder || 0, title: cTitle.trim(), text: cText.trim(), iconUrl: normalizeMediaForSave(cIcon), isActive: !!cActive };
      const res = await fetch(toUrl(`/api/medical-units/admin/${selectedUnit.id}/items`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Öğe ekleme başarısız');
      resetCreateItem(); setCreateOpen(false); await fetchItems(selectedUnit.id);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreating(false); }
  };

  const openEditItem = (it: ItemRow) => { setEditingItemId(it.id); setEITitle(it.title || ''); setEIText(it.text || ''); setEIOrder(it.orderIndex || 0); setEIActive(!!it.isActive); setEIIcon(normalizeMediaForSave(it.iconUrl) || ''); };
  const cancelEditItem = () => { setEditingItemId(''); setEITitle(''); setEIText(''); setEIOrder(0); setEIActive(true); setEIIcon(''); };
  const onSaveItem = async (id: string) => {
    if (!eITitle.trim() || !eIText.trim()) { setError('Lütfen başlık ve metin girin'); return; }
    try {
      setSavingItem(true); setError('');
      const body: any = { title: eITitle.trim(), text: eIText.trim(), orderIndex: eIOrder || 0, iconUrl: normalizeMediaForSave(eIIcon), isActive: !!eIActive };
      const res = await fetch(toUrl(`/api/medical-units/admin/items/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Öğe güncelleme başarısız');
      cancelEditItem(); if (selectedUnit) await fetchItems(selectedUnit.id);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingItem(false); }
  };
  const onDeleteItem = (id: string) => setDelItemId(id);
  const confirmDeleteItem = async () => {
    if (!delItemId) return;
    try {
      setDelItemLoading(true);
      const res = await fetch(toUrl(`/api/medical-units/admin/items/${delItemId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Öğe silme başarısız');
      setDelItemId(''); if (selectedUnit) await fetchItems(selectedUnit.id);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelItemLoading(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Tıbbi Birimler</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {units.length}</div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: başlık, açıklama" className="input" style={{ width: 300 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <button onClick={fetchUnits} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Title Light</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Title Accent</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>CTA Label</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>CTA Href</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Columns</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Dividers</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', minWidth: 180 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eTitleLight} onChange={(e) => setETitleLight(e.target.value)} />
                    ) : (row.titleLight || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 180 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eTitleAccent} onChange={(e) => setETitleAccent(e.target.value)} />
                    ) : (row.titleAccent || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 160 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eCtaLabel} onChange={(e) => setECtaLabel(e.target.value)} />
                    ) : (row.ctaLabel || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 160 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eCtaHref} onChange={(e) => setECtaHref(e.target.value)} />
                    ) : (row.ctaHref || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', width: 100 }}>
                    {editingId === row.id ? (
                      <input className="input" type="number" value={eColumns} onChange={(e) => setEColumns(parseInt(e.target.value || '0', 10))} />
                    ) : (typeof row.columnsCount === 'number' ? row.columnsCount : '-')}
                  </td>
                  <td style={{ padding: '12px 16px', width: 120 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`div-${row.id}`} type="checkbox" checked={eDividers} onChange={(e) => setEDividers(e.target.checked)} />
                        <label htmlFor={`div-${row.id}`}>Göster</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.showDividers ? '#ecfdf5' : '#fef2f2', color: row.showDividers ? '#065f46' : '#991b1b', border: `1px solid ${row.showDividers ? '#a7f3d0' : '#fecaca'}` }}>{row.showDividers ? 'Evet' : 'Hayır'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', width: 160 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`act-${row.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                        <label htmlFor={`act-${row.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => onSave(row.id)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openItems(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                        <button onClick={() => openEdit(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                        <button onClick={() => onDelete(row.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Items panel */}
      {selectedUnit && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#374151', fontWeight: 500 }}>Seçili Birim:</span>
              <span>
                <span style={{ color: '#111827', fontWeight: 600 }}>{selectedUnit.titleLight || ''}</span>{' '}
                <span style={{ color: '#2563eb', fontWeight: 700 }}>{selectedUnit.titleAccent || ''}</span>
              </span>
            </h4>
            <button type="button" onClick={() => setSelectedUnit(null)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>

          {/* Create item */}
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>Yeni Öğe</div>
              <button onClick={() => { resetCreateItem(); setCreateOpen((s) => !s); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>{createOpen ? 'Kapat' : 'Yeni Ekle'}</button>
            </div>
            {createOpen && (
              <form onSubmit={onCreateItem} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Başlık</label>
                  <input className="input" value={cTitle} onChange={(e) => setCTitle(e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Metin</label>
                  <textarea className="input" rows={2} value={cText} onChange={(e) => setCText(e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Sıra</label>
                  <input className="input" type="number" value={cOrder} onChange={(e) => setCOrder(parseInt(e.target.value || '0', 10))} />
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">İkon</label>
                  <select className="input" value={cIcon} onChange={(e) => setCIcon(e.target.value)}>
                    <option value="">Seçiniz</option>
                    {mediaOpts.map(m => (
                      <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Aktif</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input id="cActive" type="checkbox" checked={cActive} onChange={(e) => setCActive(e.target.checked)} />
                    <label htmlFor="cActive">Aktif</label>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={creating} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creating ? 0.8 : 1 }}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
                </div>
              </form>
            )}
          </div>

          {/* Items table */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>Öğeler</div>
              <button onClick={() => selectedUnit && fetchItems(selectedUnit.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>İkon</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingItems && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingItems && items.length === 0 && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {items.map(it => (
                    <tr key={it.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px' }}>
                        {editingItemId === it.id ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            <select className="input" value={eIIcon} onChange={(e) => setEIIcon(e.target.value)}>
                              <option value="">Seçiniz</option>
                              {mediaOpts.map(m => (
                                <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                              ))}
                            </select>
                            {eIIcon && (<ImgWithFallback src={eIIcon} alt="icon" height={32} />)}
                          </div>
                        ) : (
                          it.iconUrl ? <ImgWithFallback src={it.iconUrl} alt="icon" height={32} /> : '-'
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {editingItemId === it.id ? (
                          <input className="input" value={eITitle} onChange={(e) => setEITitle(e.target.value)} />
                        ) : it.title}
                      </td>
                      <td style={{ padding: '12px 16px', minWidth: 280 }}>
                        {editingItemId === it.id ? (
                          <textarea className="input" rows={2} value={eIText} onChange={(e) => setEIText(e.target.value)} />
                        ) : it.text}
                      </td>
                      <td style={{ padding: '12px 16px', width: 120 }}>
                        {editingItemId === it.id ? (
                          <input className="input" type="number" value={eIOrder} onChange={(e) => setEIOrder(parseInt(e.target.value || '0', 10))} />
                        ) : it.orderIndex}
                      </td>
                      <td style={{ padding: '12px 16px', width: 160 }}>
                        {editingItemId === it.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`iAct-${it.id}`} type="checkbox" checked={eIActive} onChange={(e) => setEIActive(e.target.checked)} />
                            <label htmlFor={`iAct-${it.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: it.isActive ? '#ecfdf5' : '#fef2f2', color: it.isActive ? '#065f46' : '#991b1b', border: `1px solid ${it.isActive ? '#a7f3d0' : '#fecaca'}` }}>{it.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {editingItemId === it.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveItem(it.id)} disabled={savingItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingItem ? 0.8 : 1 }}>{savingItem ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditItem} disabled={savingItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openEditItem(it)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                            <button onClick={() => onDeleteItem(it.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete unit confirm */}
      {delId && (
        <div role="dialog" aria-modal="true" aria-labelledby="delTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setDelId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setDelId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="delTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setDelId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu birimi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelId('')} disabled={delLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDelete} disabled={delLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: delLoading ? 0.8 : 1 }}>{delLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete item confirm */}
      {delItemId && (
        <div role="dialog" aria-modal="true" aria-labelledby="delItemTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setDelItemId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setDelItemId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="delItemTitle" style={{ margin: 0, fontSize: 18 }}>Öğe Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setDelItemId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu öğeyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelItemId('')} disabled={delItemLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDeleteItem} disabled={delItemLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: delItemLoading ? 0.8 : 1 }}>{delItemLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
    </div>
  );
};

export default MedicalUnitsYonetim;
